import { z } from 'zod';
import { CountryCodeSchema, MultiPolygonSchema, PositionSchema, SlugSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Administrative map (state → district → tehsil → village / town)     */
/* ------------------------------------------------------------------ */

export const ADMIN_LEVELS = ['state', 'district', 'subdistrict', 'town', 'village'] as const;
export const AdminLevelSchema = z.enum(ADMIN_LEVELS);
export type AdminLevel = z.infer<typeof AdminLevelSchema>;

/** Urban local body classes found in the Census town codes (UP uses all five). */
export const TOWN_CLASSES = ['M Corp.', 'NPP', 'NP', 'CB', 'ITS'] as const;
export const TownClassSchema = z.enum(TOWN_CLASSES);
export type TownClass = z.infer<typeof TownClassSchema>;

export const TOWN_CLASS_LABELS: Record<TownClass, string> = {
  'M Corp.': 'Municipal Corporation',
  NPP: 'Nagar Palika Parishad',
  NP: 'Nagar Panchayat',
  CB: 'Cantonment Board',
  ITS: 'Industrial Township',
};

/** [minLng, minLat, maxLng, maxLat] */
export const BboxSchema = z.string().transform((value, ctx) => {
  const parts = value.split(',').map((part) => Number(part.trim()));
  const [minLng, minLat, maxLng, maxLat] = parts;
  if (
    parts.length !== 4 ||
    parts.some((part) => !Number.isFinite(part)) ||
    minLng === undefined ||
    minLat === undefined ||
    maxLng === undefined ||
    maxLat === undefined ||
    minLng < -180 ||
    maxLng > 180 ||
    minLat < -90 ||
    maxLat > 90 ||
    minLng >= maxLng ||
    minLat >= maxLat
  ) {
    ctx.addIssue({ code: 'custom', message: 'bbox must be minLng,minLat,maxLng,maxLat' });
    return z.NEVER;
  }
  return [minLng, minLat, maxLng, maxLat] as const;
});
export type Bbox = readonly [number, number, number, number];

/** LGD / Census codes are digit strings. */
export const AdminCodeSchema = z.string().regex(/^\d{1,8}$/, 'Codes are digits only');

export const MapLayerQuerySchema = z
  .object({
    country: CountryCodeSchema.default('IN'),
    level: AdminLevelSchema,
    state: AdminCodeSchema.optional(),
    district: AdminCodeSchema.optional(),
    subdistrict: AdminCodeSchema.optional(),
    bbox: BboxSchema.optional(),
  })
  .refine(
    (query) =>
      query.level !== 'village' ||
      query.district !== undefined ||
      query.subdistrict !== undefined ||
      query.bbox !== undefined,
    { message: 'Villages need a district, a tehsil or a bbox', path: ['level'] },
  );
export type MapLayerQuery = z.infer<typeof MapLayerQuerySchema>;

export interface AdminUnitProperties {
  id: string;
  level: AdminLevel;
  code: string;
  name: string;
  districtCode: string | null;
  subdistrictCode: string | null;
  townClass: TownClass | null;
  areaSqKm: number;
}

export const MapSummarySchema = z.object({
  countryCode: CountryCodeSchema,
  levels: z.array(z.object({ level: AdminLevelSchema, count: z.number().int() })),
  importedAt: z.string().nullable(),
  sources: z.array(z.string()),
});
export type MapSummary = z.infer<typeof MapSummarySchema>;

export const DistrictOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
  candidateCount: z.number().int(),
});
export type DistrictOption = z.infer<typeof DistrictOptionSchema>;

/* ------------------------------------------------------------------ */
/* City candidates (every statutory town is a candidate Capsico city)  */
/* ------------------------------------------------------------------ */

export const CANDIDATE_STATUSES = ['candidate', 'shortlisted', 'rejected', 'promoted'] as const;
export const CandidateStatusSchema = z.enum(CANDIDATE_STATUSES);
export type CandidateStatus = z.infer<typeof CandidateStatusSchema>;

/** Facts fixed at import time (geometry-derived). */
export const CandidateStaticMetricsSchema = z.object({
  footprintSqKm: z.number(),
  villagesWithin5Km: z.number().int(),
  villagesWithin10Km: z.number().int(),
  villagesWithin15Km: z.number().int(),
  isDistrictHq: z.boolean(),
  isTehsilHq: z.boolean(),
});
export type CandidateStaticMetrics = z.infer<typeof CandidateStaticMetricsSchema>;

export const SCORE_FACTORS = [
  'population',
  'catchment',
  'footprint',
  'competition',
  'cluster',
] as const;
export const ScoreFactorSchema = z.enum(SCORE_FACTORS);
export type ScoreFactor = z.infer<typeof ScoreFactorSchema>;

export const ScoreBreakdownSchema = z.object({
  factor: ScoreFactorSchema,
  /** 0..1 */
  value: z.number(),
  weight: z.number(),
  /** Plain-language reason shown in the drawer. */
  note: z.string(),
});
export type ScoreBreakdown = z.infer<typeof ScoreBreakdownSchema>;

export const CityCandidateSchema = z.object({
  id: z.uuid(),
  countryCode: CountryCodeSchema,
  name: z.string(),
  slug: z.string(),
  townClass: TownClassSchema,
  censusCode: z.string(),
  districtCode: z.string(),
  districtName: z.string(),
  subdistrictCode: z.string().nullable(),
  subdistrictName: z.string().nullable(),
  population: z.number().int().nullable(),
  populationSource: z.string().nullable(),
  centroid: PositionSchema,
  metrics: CandidateStaticMetricsSchema,
  /** Computed per request from the scoring config and the current shortlist. */
  nearestBigCity: z.object({ name: z.string(), km: z.number() }).nullable(),
  nearestPicked: z.object({ name: z.string(), km: z.number() }).nullable(),
  score: z.number().int().min(0).max(100),
  status: CandidateStatusSchema,
  notes: z.string().nullable(),
  promotedTownId: z.uuid().nullable(),
  version: z.number().int().min(1),
  updatedAt: z.string(),
});
export type CityCandidate = z.infer<typeof CityCandidateSchema>;

export const CityCandidateDetailSchema = CityCandidateSchema.extend({
  breakdown: z.array(ScoreBreakdownSchema),
  footprint: MultiPolygonSchema,
  nearby: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      townClass: TownClassSchema,
      population: z.number().int().nullable(),
      status: CandidateStatusSchema,
      km: z.number(),
    }),
  ),
});
export type CityCandidateDetail = z.infer<typeof CityCandidateDetailSchema>;

export const CANDIDATE_SORTS = ['score', 'population', 'name', 'district'] as const;

export const CandidateListQuerySchema = z.object({
  country: CountryCodeSchema.default('IN'),
  status: CandidateStatusSchema.optional(),
  district: AdminCodeSchema.optional(),
  townClass: TownClassSchema.optional(),
  q: z.string().trim().min(1).max(60).optional(),
  minPopulation: z.coerce.number().int().min(0).optional(),
  maxPopulation: z.coerce.number().int().min(0).optional(),
  sort: z.enum(CANDIDATE_SORTS).default('score'),
});
export type CandidateListQuery = z.infer<typeof CandidateListQuerySchema>;

export const CandidateListSchema = z.object({
  items: z.array(CityCandidateSchema),
  total: z.number().int(),
  counts: z.record(CandidateStatusSchema, z.number().int()),
});
export type CandidateList = z.infer<typeof CandidateListSchema>;

/** Status moves an admin can make by hand; promotion has its own endpoint. */
export const MANUAL_CANDIDATE_STATUSES = ['candidate', 'shortlisted', 'rejected'] as const;

export const UpdateCandidateInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    status: z.enum(MANUAL_CANDIDATE_STATUSES).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((input) => input.status !== undefined || input.notes !== undefined, {
    message: 'Change the status or the notes',
  });
export type UpdateCandidateInput = z.infer<typeof UpdateCandidateInputSchema>;

export const PromoteCandidateInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  /** Defaults to the candidate's slug. */
  slug: SlugSchema.optional(),
  /** The town boundary is the town footprint grown by this much. */
  bufferMeters: z.number().int().min(0).max(5000).default(1000),
});
export type PromoteCandidateInput = z.infer<typeof PromoteCandidateInputSchema>;

/* ------------------------------------------------------------------ */
/* Scoring config (edited in Capsico OS; versioned)                    */
/* ------------------------------------------------------------------ */

export const ScoringConfigSchema = z
  .object({
    population: z.object({
      min: z.number().int().min(0),
      idealMin: z.number().int().min(0),
      idealMax: z.number().int().min(0),
      max: z.number().int().min(0),
    }),
    catchment: z.object({
      radiusKm: z.union([z.literal(5), z.literal(10), z.literal(15)]),
      /** Villages within the radius that count as a full score. */
      target: z.number().int().min(1),
    }),
    footprint: z.object({ targetSqKm: z.number().min(0.1).max(500) }),
    competition: z.object({
      /** Cities at or above this population are where national apps already compete. */
      bigCityPopulation: z.number().int().min(1),
      /** Distance from a big city that counts as a full score. */
      fullScoreKm: z.number().min(1).max(500),
    }),
    cluster: z.object({
      /** Within this distance of a shortlisted or promoted town = full score. */
      fullScoreKm: z.number().min(1).max(500),
      /** Beyond this distance = zero. */
      zeroScoreKm: z.number().min(1).max(1000),
    }),
    weights: z.record(ScoreFactorSchema, z.number().min(0).max(100)),
  })
  .refine(
    (config) =>
      config.population.min <= config.population.idealMin &&
      config.population.idealMin <= config.population.idealMax &&
      config.population.idealMax <= config.population.max,
    { message: 'Population band must be min ≤ ideal from ≤ ideal to ≤ max', path: ['population'] },
  )
  .refine((config) => config.cluster.fullScoreKm < config.cluster.zeroScoreKm, {
    message: 'Cluster full-score distance must be below the zero-score distance',
    path: ['cluster'],
  })
  .refine((config) => SCORE_FACTORS.some((factor) => (config.weights[factor] ?? 0) > 0), {
    message: 'At least one weight must be above zero',
    path: ['weights'],
  });
export type ScoringConfig = z.infer<typeof ScoringConfigSchema>;

/**
 * Recommended rules, tuned on the real Uttar Pradesh import (624 towns): full scores sit near
 * the top 10% of each factor, so the best towns spread out instead of all tying at the top.
 */
export const DEFAULT_SCORING_CONFIG: ScoringConfig = {
  population: { min: 20_000, idealMin: 80_000, idealMax: 300_000, max: 800_000 },
  catchment: { radiusKm: 10, target: 350 },
  footprint: { targetSqKm: 25 },
  competition: { bigCityPopulation: 500_000, fullScoreKm: 150 },
  cluster: { fullScoreKm: 40, zeroScoreKm: 150 },
  weights: { population: 35, catchment: 20, footprint: 10, competition: 20, cluster: 15 },
};

export const ScoringConfigRecordSchema = z.object({
  value: ScoringConfigSchema,
  version: z.number().int().min(1),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type ScoringConfigRecord = z.infer<typeof ScoringConfigRecordSchema>;

export const SaveScoringConfigInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  value: ScoringConfigSchema,
});
export type SaveScoringConfigInput = z.infer<typeof SaveScoringConfigInputSchema>;
